import type { AgentConnection, AgentProvider, AgentUsageBalance } from '@/api'

export const AGENT_PROVIDER_NAMES: Record<AgentProvider, string> = {
    claude: 'Claude',
    openai: 'OpenAI',
    grok: 'Grok',
    openrouter: 'OpenRouter',
}

/** Display order and tie-break for connections with equal priority. */
export const AGENT_PROVIDERS: AgentProvider[] = ['claude', 'openai', 'grok', 'openrouter']

/** The providers a subscription login can be connected for, besides an API key. */
export type SubscriptionProvider = Exclude<AgentProvider, 'openrouter'>

export function hasSubscription(provider: AgentProvider): provider is SubscriptionProvider {
    return provider !== 'openrouter'
}

/**
 * How each provider's subscription sign-in reads: what the plan is called, what a login on it is called on a card,
 * who the person signs in with, and where an API key comes from.
 */
export const AGENT_SUBSCRIPTIONS: Record<
    SubscriptionProvider,
    { plan: string; seat: string; vendor: string; keyLabel: string }
> = {
    claude: { plan: 'Claude Pro/Max', seat: 'Pro/Max seat', vendor: 'Anthropic', keyLabel: 'Console API key' },
    openai: { plan: 'ChatGPT plan', seat: 'ChatGPT seat', vendor: 'OpenAI', keyLabel: 'API key' },
    grok: { plan: 'SuperGrok plan', seat: 'SuperGrok seat', vendor: 'xAI', keyLabel: 'API key' },
}

export function agentAuthLabel(row: AgentConnection): string {
    if (row.status === 'unavailable') return 'Credential unavailable'
    if (!hasSubscription(row.provider)) return 'API key'
    const subscription = AGENT_SUBSCRIPTIONS[row.provider]
    return row.authKind === 'oauth' ? subscription.seat : subscription.keyLabel
}

export function agentOwnerPath(row: AgentConnection): string {
    if (row.layer === 'enterprise') return '/enterprise'
    return `/${row.layer === 'team' ? 'teams' : 'members'}/${encodeURIComponent(row.layerId)}`
}

export function usageBarClass(percent: number): string {
    if (percent >= 90) return 'bg-neon-magenta'
    if (percent >= 70) return 'bg-warning-amber'
    return 'bg-electric-cyan'
}

/** A day's spend bar: the same cyan as an unstrained quota when something was spent, the muted ground when nothing was. */
export function usageDayClass(usdCents: number): string {
    return usdCents > 0 ? 'bg-electric-cyan' : 'bg-muted'
}

export function formatResetsIn(iso: string | null, now = Date.now()): string | null {
    if (!iso) return null
    const ms = new Date(iso).getTime() - now
    if (!Number.isFinite(ms)) return null
    if (ms <= 0) return 'Resets soon'
    const minutes = Math.round(ms / 60_000)
    if (minutes < 60) return `Resets in ${minutes}m`
    const hours = Math.round(minutes / 60)
    if (hours < 48) return `Resets in ${hours}h`
    return `Resets in ${Math.round(hours / 24)}d`
}

export function formatDaysRemaining(balance: AgentUsageBalance): string | null {
    if (balance.remainingCents === null) {
        return balance.limitCents === null ? 'No credit cap on this key.' : null
    }
    if (balance.remainingCents <= 0) return 'No credits remaining.'
    if (balance.averageDailyCents === null || balance.averageDailyCents <= 0) {
        return 'No recent usage to estimate remaining days.'
    }
    const days = balance.daysRemaining
    if (days === null) return null
    if (days > 365) return 'More than a year left at current usage.'
    if (days === 0) return 'Less than a day left at current usage.'
    if (days === 1) return 'About 1 day left at current usage.'
    return `About ${days} days left at current usage.`
}
